import React from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { connect, useSelector } from 'react-redux';
import { useDecision } from '@optimizely/react-sdk';

import PatLibGlobalNav from '@starbucks-web/pattern-library/lib/components/global-nav';
import PreventFocus from '@starbucks-web/pattern-library/lib/components/prevent-focus';

import { FEATURES } from 'shared/app/components/optimizely/optimizely-keys';

import {
  doLogin as doLoginAction,
  doLogoutAndForget as doLogoutAction,
  setHamburgerNavIsOpen as setHamburgerNavIsOpenAction,
} from 'shared/app/shell/state/action-creators/app-lifecycle';
import { hamburgerNavIsOpenSelector } from 'shared/app/shell/state/selectors/app-ui';
import {
  routeResultSelector,
  routeSelector,
} from 'shared/app/state/selectors/routes';
import { legacyBaseUrlSelector } from 'shared/app/state/selectors/locales';

import { signedInSelector } from 'shared/app/bundles/user';
import {
  accountsMessages,
  commonMessages,
  sharedCallsToAction,
} from 'shared/app/messages';
import {
  globalNavAccountNavSelector,
  globalNavMainNavLinksSelector,
} from 'shared/app/state/selectors/global-nav';

import { getButtonProps } from './get-button-props';
import messages from './messages';
import { navItemsFormatter } from './nav-item-formatters';
import styles from './styles.cssm';

// eslint-disable-next-line max-statements
const GlobalNav = ({
  isCrateLayout,
  preventFocusTargets,
  setHamburgerNavIsOpen,
  doLogin,
  doLogout,
  navProps: { logoOnly = false, useMaxWidthLayout = false },
}) => {
  const [nextGenLoyaltyEnabled] = useDecision(FEATURES.NEXT_GEN_LOYALTY);

  const { formatMessage } = useIntl();

  const accountNavConfig = useSelector(globalNavAccountNavSelector);
  const hamburgerNavIsOpen = useSelector(hamburgerNavIsOpenSelector);
  const mainNavLinks = useSelector(
    globalNavMainNavLinksSelector(nextGenLoyaltyEnabled)
  );
  const route = useSelector(routeSelector);
  const routeResult = useSelector(routeResultSelector);
  const isUserAuthenticated = useSelector(signedInSelector);
  const legacyBaseUrl = useSelector(legacyBaseUrlSelector);

  const hideMobileBottomShadow =
    isCrateLayout && !routeResult?.showGlobalNavMobileBottomShadow;

  const navItems = navItemsFormatter({
    logoOnly,
    navItems: mainNavLinks,
    currentRoute: route,
  });

  const getAccountNavItems = () => {
    if (logoOnly) {
      return null;
    }
    const { subNavItems } = accountNavConfig;

    const standAloneAccountSubNavItems = subNavItems.map(
      ({ url, ...subNavItem }) => ({
        ...subNavItem,
        url,
      })
    );

    return {
      children: formatMessage(accountsMessages.account),
      'data-e2e': 'accountHamburgerNavPushViewBtn',
      subNavItems: navItemsFormatter({
        currentRoute: route,
        navItems: standAloneAccountSubNavItems || subNavItems,
      }),
    };
  };

  const logoProps = {
    ariaLabel: formatMessage(commonMessages.logoLabel),
    href: legacyBaseUrl || '/',
  };

  const className = classNames(styles.globalNav, {
    [styles.globalNavOpen]: hamburgerNavIsOpen,
    [styles.globalNavFixed]: isCrateLayout,
  });

  const { findAStorePin, joinNowButton, signInButton, signOutButton } = logoOnly
    ? {}
    : getButtonProps({
        doLogin,
        doLogout,
        formatMessage,
        route,
      });

  return (
    <PreventFocus enabled={preventFocusTargets.header && !hamburgerNavIsOpen}>
      <PatLibGlobalNav
        accountNav={getAccountNavItems()}
        className={className}
        findAStorePin={findAStorePin}
        fixedWhenHamburgerNavOpen={false}
        headerProps={{
          'data-e2e': 'navigationBar',
        }}
        hideMobileBottomShadow={hideMobileBottomShadow}
        isUserAuthenticated={isUserAuthenticated}
        joinNowButton={joinNowButton}
        logoProps={logoProps}
        menuButtonProps={{
          labelWhenClosed: formatMessage(sharedCallsToAction.openMenuButton),
          labelWhenOpen: formatMessage(sharedCallsToAction.closeMenuButton),
          'data-e2e': 'navMenuButton',
        }}
        navAriaLabel={formatMessage(messages.navAriaLabel)}
        navItems={navItems}
        onHamburgerNavIsOpenChange={(isOpen) => setHamburgerNavIsOpen(isOpen)}
        pushViewButtonLabels={{
          pushViewButtonLabelLeft: formatMessage(
            messages.pushViewButtonLabelLeft
          ),
          pushViewButtonLabelRightFormatter: (navItemText) =>
            formatMessage(messages.pushViewButtonLabelRight, {
              navItemText,
            }),
        }}
        signInButton={signInButton}
        signOutButton={signOutButton}
        useCrateLayout={isCrateLayout}
        useHamburgerMenuButton={logoOnly ? false : true}
        useMaxWidthLayout={useMaxWidthLayout}
      />
    </PreventFocus>
  );
};

const actions = {
  doLogin: doLoginAction,
  doLogout: doLogoutAction,
  setHamburgerNavIsOpen: setHamburgerNavIsOpenAction,
};

export default connect(null, actions)(GlobalNav);
